import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import '../Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const colors = {
  bg: '#0f172a',
  card: '#1f2937',
  border: '#374151',
  text: '#f9fafb',
  textMuted: '#9ca3af',
  blue: '#3b82f6',
  green: '#16a34a',
  red: '#dc2626',
};

const ALLOWED_EMAILS = ['bobby_malik@hotmail.com'];

export const SpxReport: React.FC = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'downloading' | 'error' | 'no-auth'>('loading');
  const [error, setError] = useState('');

  const date = searchParams.get('date');
  const snapshot = searchParams.get('snapshot');
  const version = searchParams.get('version');  // 'v2' for V2 reports
  const userEmail = (user as any)?.email?.toLowerCase() || '';
  const isV2 = version === 'v2';

  useEffect(() => {
    if (!token) {
      setStatus('no-auth');
      return;
    }
    if (!ALLOWED_EMAILS.includes(userEmail)) {
      setError('You do not have access to this report.');
      setStatus('error');
      return;
    }
    if (!date) {
      setError('Missing date parameter');
      setStatus('error');
      return;
    }

    const fetchAndDownload = async () => {
      setStatus('downloading');
      try {
        const dateParam = date ? `&date=${date}` : '';
        const endpoint = isV2 ? '/spx/signals-v2' : '/spx/signals';
        const resp = await fetch(
          `${API_BASE_URL}${endpoint}?word=true${dateParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${resp.status}`);
        }

        const data = await resp.json();
        if (data.wordUrl) {
          window.location.href = data.wordUrl;
        } else {
          throw new Error('No Word report available for this date');
        }
      } catch (err: any) {
        // Never expose raw AWS/infrastructure errors to users
        const msg = err.message || '';
        const userMsg = msg.includes('AccessDenied') || msg.includes('403') || msg.includes('Forbidden')
          ? 'The report could not be downloaded. Please try again in a moment.'
          : msg.includes('No Word report')
            ? 'No report is available for this date yet. Reports are generated after each research run.'
            : 'Unable to download the report. Please try again later.';
        setError(userMsg);
        setStatus('error');
      }
    };

    fetchAndDownload();
  }, [token, date, snapshot, isV2]);

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '3rem 1.5rem', color: colors.text, textAlign: 'center' }}>
        {status === 'no-auth' && (
          <>
            <h2>Sign In Required</h2>
            <p style={{ color: colors.textMuted }}>Please sign in to download the SPX research report.</p>
            <a href="/login" style={{
              display: 'inline-block', padding: '0.75rem 1.5rem', borderRadius: 6,
              background: colors.blue, color: '#fff', textDecoration: 'none', fontWeight: 600,
            }}>Sign In</a>
          </>
        )}

        {status === 'loading' && <p style={{ color: colors.textMuted }}>Preparing download...</p>}

        {status === 'downloading' && (
          <>
            <h2>Downloading Report</h2>
            <p style={{ color: colors.textMuted }}>
              Your SPX {isV2 ? 'V2 Time Horizon ' : ''}Research Report for {date} is being downloaded.
              If the download doesn't start automatically, please wait a moment.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 style={{ color: colors.red }}>Download Failed</h2>
            <p style={{ color: colors.textMuted }}>{error}</p>
            <a href={isV2 ? "/spx/v2" : "/spx/signals"} style={{
              display: 'inline-block', padding: '0.75rem 1.5rem', borderRadius: 6,
              background: colors.blue, color: '#fff', textDecoration: 'none', fontWeight: 600,
              marginTop: '1rem',
            }}>View Research</a>
          </>
        )}
      </div>
    </div>
  );
};
